import { useContext, useState } from "react";
import { useHistory } from "react-router-dom";
import { QueryClient, useMutation, useQueryClient } from "@tanstack/react-query";
import { isEqual } from "lodash";
import { toast } from "sonner";
import { ApiError, apiErrorMessage, isApiError } from "@/services/bedrock/apiError";
import { DesignPackageServices } from "@/services/bedrock/DesignPackageServices";
import { BackgroundPolicy } from "@/services/bedrock/dtos/BackgroundAsset";
import {
  ActorRef,
  DesignPackageDetail,
  GenerateDesignPackageRequest,
  PublishDesignPackageRequest,
} from "@/services/bedrock/dtos/DesignPackage";
import { DesignPackageActivityEvent } from "@/services/bedrock/dtos/DesignPackageActivity";
import { Page } from "@/services/bedrock/dtos/Page";
import { designKeys } from "@/services/bedrock/queryKeys";
import { URLS } from "@/utils/routes";
import { PromptStudioScopeContext } from "../PromptStudioScope";
import { SlideBusy } from "../slideState";
import { isSelfActor, useViewer, Viewer, viewerActor } from "../viewer";

const GENERIC_ERROR = "Something went wrong. Try again.";
// Upper bound on how long the composer waits for its `refined` activity before letting go.
const REFINED_EVENT_TIMEOUT_MS = 20_000;

export const inFlightMessage = (name: string) => `Wait for ${name}'s change to finish.`;

function actorName(e: ApiError): string | null {
  const actor = (e.details as { actor?: ActorRef | null } | undefined)?.actor;
  return actor?.name ?? null;
}

function activityEvents(queryClient: QueryClient, id: number): DesignPackageActivityEvent[] {
  const data = queryClient.getQueryData<{ pages?: Page<DesignPackageActivityEvent>[] }>(designKeys.activity(id));
  return (data?.pages ?? []).flatMap(page => page.content);
}

function refinedEventIds(queryClient: QueryClient, id: number): Set<number> {
  return new Set(
    activityEvents(queryClient, id)
      .filter(e => e.action === "refined")
      .map(e => e.id)
  );
}

/** Resolves once a new `refined` event by the viewer is in the activity cache (cable or refetch). */
function waitForRefinedEvent(queryClient: QueryClient, id: number, before: Set<number>, viewer: Viewer) {
  return new Promise<void>(resolve => {
    const key = designKeys.activity(id);
    const arrived = () =>
      activityEvents(queryClient, id).some(
        e => e.action === "refined" && !before.has(e.id) && isSelfActor(e.actor, viewer)
      );
    let unsubscribe = () => {};
    const timer = setTimeout(() => done(), REFINED_EVENT_TIMEOUT_MS);
    function done() {
      unsubscribe();
      clearTimeout(timer);
      resolve();
    }
    if (arrived()) return done();
    unsubscribe = queryClient.getQueryCache().subscribe(event => {
      if (isEqual(event.query.queryKey, key) && arrived()) done();
    });
  });
}

export interface RefineVariables {
  instructions: string;
  itemId?: number;
  /** Defaults to the slide's pending candidate; `null` sends none, so the change starts from the active version. */
  baseDesignTemplateId?: number | null;
}

/**
 * `generate`, `retry`, and `duplicate` take the package id as their variables; everything else acts
 * on `id`. Every success writes the returned detail into the cache and invalidates the lists.
 */
export function useDesignPackageMutations(id: number | null = null) {
  const queryClient = useQueryClient();
  const history = useHistory();
  const adminScope = useContext(PromptStudioScopeContext);
  const viewer = useViewer();
  const [busy, setBusy] = useState<SlideBusy>({});

  const packageId = () => {
    if (id == null) throw new Error("useDesignPackageMutations needs a package id for this mutation");
    return id;
  };
  const detail = () => (id == null ? undefined : queryClient.getQueryData<DesignPackageDetail>(designKeys.detail(id)));
  const setDetail = (pkg: DesignPackageDetail | undefined) => {
    if (pkg) queryClient.setQueryData(designKeys.detail(pkg.id), pkg);
  };
  const invalidate = (...keys: (readonly unknown[])[]) =>
    keys.forEach(queryKey => queryClient.invalidateQueries({ queryKey }));
  const previewsPrefix = () => designKeys.previews(packageId(), null).slice(0, 2);

  const onPackageSaved = (pkg: DesignPackageDetail) => {
    setDetail(pkg);
    queryClient.invalidateQueries({ queryKey: designKeys.all });
  };

  const onError = (error: unknown) => {
    toast.error(apiErrorMessage(error, GENERIC_ERROR));
  };

  const inFlightToast = (error: ApiError) => {
    const name = actorName(error);
    toast.error(name ? inFlightMessage(name) : "Wait for the current change to finish.");
    if (id != null) invalidate(designKeys.detail(id));
  };

  const me: ActorRef = viewerActor(viewer);

  const duplicate = useMutation({
    mutationFn: (vars: number | { id: number; coachId?: number }) =>
      typeof vars === "number"
        ? DesignPackageServices.duplicate(vars)
        : DesignPackageServices.duplicate(vars.id, vars.coachId != null ? { coachId: vars.coachId } : {}),
    onSuccess: onPackageSaved,
    onError,
  });

  // No shared toast: New Design shows its own message and maps validation errors onto the form.
  const generate = useMutation({
    mutationFn: (request: GenerateDesignPackageRequest) => DesignPackageServices.generate(request),
    onSuccess: onPackageSaved,
  });

  const retry = useMutation({
    mutationFn: (packageToRetry: number) => DesignPackageServices.retry(packageToRetry),
    onSuccess: onPackageSaved,
    onError,
  });

  const rename = useMutation({
    mutationFn: (name: string) => DesignPackageServices.update(packageId(), { name }),
    onMutate: async (name: string) => {
      const previous = detail();
      if (previous) setDetail({ ...previous, name });
      return { previous };
    },
    onSuccess: onPackageSaved,
    onError: (error, _name, context) => {
      setDetail(context?.previous);
      if (isApiError(error, "validation_error")) toast.error("Names must be 1 to 120 characters.");
      else onError(error);
    },
  });

  const refine = useMutation({
    mutationFn: async ({ instructions, itemId, baseDesignTemplateId }: RefineVariables) => {
      const pkgId = packageId();
      const before = refinedEventIds(queryClient, pkgId);
      const base =
        baseDesignTemplateId === null
          ? undefined
          : baseDesignTemplateId ??
            (itemId != null ? detail()?.items.find(i => i.id === itemId)?.candidateTemplate?.id : undefined);
      const saved = await DesignPackageServices.refine(pkgId, {
        instructions,
        ...(itemId != null ? { designPackageItemId: itemId } : {}),
        ...(base != null ? { baseDesignTemplateId: base } : {}),
      });
      setDetail(saved);
      invalidate(designKeys.activity(pkgId), previewsPrefix());
      await waitForRefinedEvent(queryClient, pkgId, before, viewer);
      return saved;
    },
    onMutate: async ({ itemId }: RefineVariables) => {
      const pkgId = packageId();
      await queryClient.cancelQueries({ queryKey: designKeys.detail(pkgId) });
      const previous = detail();
      if (previous) {
        const itemIds = itemId != null ? [itemId] : previous.items.map(i => i.id);
        setDetail({
          ...previous,
          inFlight: { kind: "refine", itemIds, actor: me, startedAt: new Date().toISOString() },
          items: previous.items.map(i => (itemIds.includes(i.id) ? { ...i, status: "queued" } : i)),
        });
      }
      return { previous };
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: designKeys.all }),
    onError: (error, _vars, context) => {
      setDetail(context?.previous);
      if (isApiError(error, "refine_in_progress")) {
        const details = error.details as { actor?: ActorRef; designPackageItemIds?: number[] } | undefined;
        const previous = context?.previous;
        if (previous && details?.actor) {
          setDetail({
            ...previous,
            inFlight: {
              kind: "refine",
              itemIds: details.designPackageItemIds ?? [],
              actor: details.actor,
              startedAt: new Date().toISOString(),
            },
          });
        }
        toast.error(`${details?.actor?.name ?? "Someone"} is already refining this design. Wait for it to finish.`);
        invalidate(designKeys.detail(packageId()));
      } else if (isApiError(error, "package_not_refinable")) {
        toast.error("This design isn't ready for changes yet.");
      } else {
        toast.error("We couldn't send that change. Try again.");
      }
    },
  });

  const revisionOptions = (kind: "approving" | "discarding") => ({
    mutationFn: (itemId?: number) => {
      const request = itemId != null ? { designPackageItemId: itemId } : {};
      return kind === "approving"
        ? DesignPackageServices.approveRevision(packageId(), request)
        : DesignPackageServices.discardRevision(packageId(), request);
    },
    onMutate: (itemId?: number) => {
      const itemIds = itemId != null ? [itemId] : detail()?.items.map(i => i.id) ?? [];
      setBusy(current => ({ ...current, ...Object.fromEntries(itemIds.map(i => [i, kind])) }));
      return { itemIds };
    },
    onSuccess: (pkg: DesignPackageDetail) => {
      onPackageSaved(pkg);
      invalidate(designKeys.activity(pkg.id), previewsPrefix());
    },
    onError: (error: unknown) => {
      if (isApiError(error, "no_candidate")) {
        const name = actorName(error);
        toast.error(name ? `That version was already handled by ${name}.` : "That version was already handled.");
        invalidate(designKeys.detail(packageId()));
      } else if (isApiError(error, "refine_in_progress")) {
        inFlightToast(error);
      } else {
        onError(error);
      }
    },
    onSettled: (
      _data: DesignPackageDetail | undefined,
      _error: unknown,
      _itemId: number | undefined,
      context: { itemIds: number[] } | undefined
    ) => {
      setBusy(current => {
        const next = { ...current };
        (context?.itemIds ?? []).forEach(i => delete next[i]);
        return next;
      });
    },
  });
  const approve = useMutation(revisionOptions("approving"));
  const discard = useMutation(revisionOptions("discarding"));

  // Mapped errors are left to the caller: the Publish dialog shows them inline.
  const publish = useMutation({
    mutationFn: (request: PublishDesignPackageRequest) => DesignPackageServices.publish(packageId(), request),
    onSuccess: pkg => {
      onPackageSaved(pkg);
      invalidate(designKeys.activity(pkg.id));
      toast.success("Published. Your players will see it in their library.");
    },
    onError: error => {
      if (!isApiError(error, "not_completed") && !isApiError(error, "refine_in_progress")) onError(error);
    },
  });

  const unpublish = useMutation({
    mutationFn: ({ deleteScheduledPosts }: { deleteScheduledPosts: boolean }) =>
      DesignPackageServices.unpublish(packageId(), { deleteScheduledPosts }),
    onSuccess: ({ deletedScheduledPosts, ...saved }) => {
      onPackageSaved(saved);
      invalidate(designKeys.activity(saved.id));
      const removed = deletedScheduledPosts ?? 0;
      toast.success(removed > 0 ? `Unpublished and removed ${removed} scheduled posts` : "Unpublished");
    },
    onError: error => (isApiError(error, "refine_in_progress") ? inFlightToast(error) : onError(error)),
  });

  const archive = useMutation({
    mutationFn: () => DesignPackageServices.archive(packageId()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: designKeys.all });
      invalidate(designKeys.detail(packageId()));
      toast.success("Archived.");
      history.push(adminScope ? URLS.admin.promptStudio : URLS.coach.promptStudio);
    },
    onError: error => (isApiError(error, "refine_in_progress") ? inFlightToast(error) : onError(error)),
  });

  const restore = useMutation({
    mutationFn: () => DesignPackageServices.restore(packageId()),
    onSuccess: pkg => {
      onPackageSaved(pkg);
      toast.success("Restored.");
    },
    onError,
  });

  const setBackgroundPolicy = useMutation({
    mutationFn: (policy: BackgroundPolicy) => DesignPackageServices.update(packageId(), { backgroundPolicy: policy }),
    onMutate: (policy: BackgroundPolicy) => {
      const previous = detail();
      if (previous) setDetail({ ...previous, backgroundPolicy: policy });
      return { previous };
    },
    onSuccess: pkg => {
      onPackageSaved(pkg);
      invalidate(designKeys.activity(pkg.id));
    },
    onError: (error, _policy, context) => {
      setDetail(context?.previous);
      onError(error);
    },
  });

  return {
    duplicate,
    generate,
    retry,
    rename,
    refine,
    approve,
    discard,
    publish,
    unpublish,
    archive,
    restore,
    setBackgroundPolicy,
    /** Slides with a Keep or Discard request in flight. */
    busy,
  };
}
